import { useContext, useEffect, useMemo, useState } from 'react'
import { enqueueSnackbar } from 'notistack'

import { AppContext } from '/providers/app-context'
import { getCurrentTemplateId, setCurrentTemplateId } from '/utils/current-template'
import {
  getClientStatisticsAdoption,
  getClientStatisticsInstalls,
  getClientStatisticsPassActivity,
  getClientStatisticsPassTypes,
  getClientStatisticsScans,
  getClientStatisticsTemplateChanges
} from '/providers/clients-api'

export const periodOptions = [
  { label: '1 day', value: 1 },
  { label: '1 week', value: 7 },
  { label: '1 month', value: 31 },
  { label: '3 months', value: 92 },
  { label: '6 months', value: 183 },
  { label: '12 months', value: 365 }
]

const emptyTotals = { issued: 0, updated: 0, notified: 0 }
const emptyExtraTotals = { templatesCreated: 0, templateUpdates: 0, appleInstalls: 0, googleInstalls: 0, scans: 0, activePasses: 0, expiredPasses: 0 }

const statisticsCacheTtlMs = 5 * 60 * 1000
const statisticsCache = new Map()

const reportConfigs = {
  passActivity: {
    loadReport: getClientStatisticsPassActivity,
    defaultStatistics: {
      trend: [],
      totals: emptyTotals
    },
    mapResult: result => ({
      trend: result.trend || [],
      totals: result.totals || emptyTotals
    })
  },
  adoption: {
    loadReport: getClientStatisticsAdoption,
    defaultStatistics: {
      adoption: [],
      extraTotals: emptyExtraTotals
    },
    mapResult: result => ({
      adoption: result.adoption || [],
      extraTotals: result.extraTotals || emptyExtraTotals
    })
  },
  templateChanges: {
    loadReport: getClientStatisticsTemplateChanges,
    defaultStatistics: {
      templateLifecycle: [],
      extraTotals: emptyExtraTotals
    },
    mapResult: result => ({
      templateLifecycle: result.templateLifecycle || [],
      extraTotals: result.extraTotals || emptyExtraTotals
    })
  },
  installs: {
    loadReport: getClientStatisticsInstalls,
    defaultStatistics: {
      installTrend: [],
      extraTotals: emptyExtraTotals
    },
    mapResult: result => ({
      installTrend: result.installTrend || [],
      extraTotals: result.extraTotals || emptyExtraTotals
    })
  },
  scans: {
    loadReport: getClientStatisticsScans,
    defaultStatistics: {
      scanTrend: [],
      scanTrendSeries: [],
      extraTotals: emptyExtraTotals
    },
    mapResult: result => ({
      scanTrend: result.scanTrend || [],
      scanTrendSeries: result.scanTrendSeries || [],
      extraTotals: result.extraTotals || emptyExtraTotals
    })
  },
  passTypes: {
    loadReport: getClientStatisticsPassTypes,
    defaultStatistics: {
      passTypeMix: []
    },
    mapResult: result => ({
      passTypeMix: result.passTypeMix || []
    })
  }
}

const getCacheKey = ({ reportType, apiSource, selectedClientId, periodDays, templateId }) => (
  JSON.stringify({
    reportType,
    apiSource,
    selectedClientId: selectedClientId ?? null,
    periodDays,
    templateId: templateId || null
  })
)

const useClientStatistics = reportType => {
  const { user, apiSource, apiKey, templates, loadingTemplates, selectedClientId } = useContext(AppContext)
  const [periodDays, setPeriodDays] = useState(31)
  const [templateId, setTemplateIdState] = useState(getCurrentTemplateId)
  const reportConfig = reportConfigs[reportType]
  const [statistics, setStatistics] = useState(reportConfig.defaultStatistics)
  const [loading, setLoading] = useState(false)

  const selectableTemplates = useMemo(
    () => (templates || []).filter(template => !template.archived).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [templates]
  )

  const setTemplateId = nextTemplateId => {
    setTemplateIdState(nextTemplateId)
    setCurrentTemplateId(nextTemplateId)
  }

  const cacheKey = useMemo(() => getCacheKey({ reportType, apiSource, selectedClientId, periodDays, templateId }), [reportType, apiSource, selectedClientId, periodDays, templateId])

  useEffect(() => {
    let active = true

    const loadStatistics = async () => {
      const cached = statisticsCache.get(cacheKey)

      if (cached) {
        setStatistics(cached.value)
      } else {
        setStatistics(reportConfig.defaultStatistics)
      }

      if (cached && (Date.now() - cached.ts) < statisticsCacheTtlMs) {
        setLoading(false)
        return
      }

      setLoading(true)
      const result = await reportConfig.loadReport({
        apiSource,
        apiKey,
        period_days: periodDays,
        template_id: templateId || undefined
      })

      if (!active) return

      setLoading(false)

      if (result.ok) {
        const mappedResult = reportConfig.mapResult(result)
        statisticsCache.set(cacheKey, { ts: Date.now(), value: mappedResult })
        setStatistics(mappedResult)
      }
      else {
        if (Number(result.error?.status_code) === 429) return

        enqueueSnackbar({
          message: result.error?.message ?? result.error ?? 'Unable to load statistics',
          variant: 'error'
        })
      }
    }

    if (user?.permissions.client_admin && !loadingTemplates) {
      loadStatistics()
    }

    return () => {
      active = false
    }
  }, [user, apiSource, apiKey, periodDays, templateId, loadingTemplates, selectedClientId, cacheKey, reportConfig])

  return {
    loading,
    periodDays,
    setPeriodDays,
    selectableTemplates,
    statistics,
    templateId,
    setTemplateId
  }
}

export default useClientStatistics
